import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { formatDateLikeForDisplay } from "util/dates";

import {
  EMPTY,
  Resolved870012Content,
  ResolvedConsolidatedSite,
  ResolvedContact,
  ResolvedEpisodicEvent,
  ResolvedHsmNotification,
  ResolvedSiteClosure,
} from "./frozenSnapshot";
import {
  CLOSURE_TYPE_LABELS,
  EPISODIC_PROJECT_TYPE_LABELS,
  HSM_LAND_BASED_UNIT_LABELS,
  HSM_REASON_LABELS,
  WASTE_UNIT_LABELS,
} from "./rcraNotificationLabels";

function formatDate(value: string | null | undefined): string {
  return value ? formatDateLikeForDisplay(value) : "";
}

function formatBool(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value ? "Yes" : "No";
}

function CodeChips({ codes }: { codes: string[] }) {
  if (codes.length === 0) return <>{""}</>;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {codes.map((code) => (
        <Chip key={code} label={code} size="small" />
      ))}
    </Stack>
  );
}

function contactName(contact: ResolvedContact | null | undefined): string {
  return [contact?.first, contact?.last].filter(Boolean).join(" ");
}

function contactPhone(contact: ResolvedContact | null | undefined): string {
  return contact?.phones?.[0]?.number ?? "";
}

function formatConsolidatedSiteAddress(site: ResolvedConsolidatedSite): string {
  const street = [site.streetAddress1, site.streetAddress2].filter(Boolean).join(" ");
  const cityState = [site.city, site.state].filter(Boolean).join(", ");
  return [street, cityState, site.zipCode, site.country].filter(Boolean).join(" ");
}

/** §13 Episodic Generation, read-only. */
function FrozenEpisodicSection({ event }: { event: ResolvedEpisodicEvent }) {
  const projectTypes = event.projectTypes ?? [];
  const wasteLines = event.wasteLines ?? [];
  const emergencyContact = event.emergencyContact ?? null;
  const emergencyContactName = contactName(emergencyContact);
  const emergencyContactEmail = emergencyContact?.email ?? "";
  const emergencyContactPhone = contactPhone(emergencyContact);
  const hasEmergencyContact = Boolean(
    emergencyContactName || emergencyContactEmail || emergencyContactPhone
  );

  return (
    <Box>
      <Typography variant="subtitle1" mb={2}>
        Episodic Generation
      </Typography>
      <Grid container columnSpacing={3} rowSpacing={2} mb={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField label="Planned Event" text={formatBool(event.isPlannedEpisodicEvent)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField label="Beginning Date" text={formatDate(event.beginningDate)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField label="End Date" text={formatDate(event.endDate)} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <DisplayOnlyField
            label="Project Types"
            text={projectTypes.map((type) => EPISODIC_PROJECT_TYPE_LABELS[type]).join(", ")}
          />
        </Grid>
        {event.otherDescription ? (
          <Grid size={{ xs: 12 }}>
            <DisplayOnlyField label="Other Description" text={event.otherDescription} />
          </Grid>
        ) : null}
        {hasEmergencyContact ? (
          <>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField label="Emergency Contact" text={emergencyContactName} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField label="Contact Email" text={emergencyContactEmail} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField label="Contact Phone" text={emergencyContactPhone} />
            </Grid>
          </>
        ) : null}
      </Grid>

      <Typography variant="subtitle2" gutterBottom>
        Waste Lines
      </Typography>
      {wasteLines.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {EMPTY}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {wasteLines.map((line, index) => (
            <Grid container columnSpacing={3} rowSpacing={1} key={index}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Description" text={line.wasteDescription ?? ""} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                <DisplayOnlyField
                  label="Estimated Quantity"
                  text={line.estimatedQuantity?.toString() ?? ""}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                <DisplayOnlyField
                  label="Unit"
                  text={line.quantityUnit ? WASTE_UNIT_LABELS[line.quantityUnit] : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                <DisplayOnlyField
                  label="Federal Waste Codes"
                  renderValue={() => <CodeChips codes={line.federalWasteCodes ?? []} />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
                <DisplayOnlyField
                  label="State Waste Codes"
                  renderValue={() => <CodeChips codes={line.stateWasteCodes ?? []} />}
                />
              </Grid>
            </Grid>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * §14 LQG Consolidation of VSQG Hazardous Waste, read-only. Like the other
 * sections here it renders only when filed — i.e. when the site is a consolidator
 * (`isLqgConsolidator === true`) in the snapshot.
 */
function FrozenLqgConsolidationSection({ sites }: { sites: ResolvedConsolidatedSite[] }) {
  return (
    <Box>
      <Typography variant="subtitle1" mb={2}>
        LQG Consolidation of VSQG Hazardous Waste
      </Typography>
      <Typography variant="subtitle2" gutterBottom>
        Consolidated VSQG sites
      </Typography>
      {sites.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {EMPTY}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {sites.map((site, index) => (
            <Grid container columnSpacing={3} rowSpacing={1} key={index}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Consolidated Site EPA ID" text={site.epaIdNumber ?? ""} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Consolidated Site Name" text={site.siteName ?? ""} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Site Address" text={formatConsolidatedSiteAddress(site)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Contact Name" text={contactName(site.contact)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Contact Phone" text={contactPhone(site.contact)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField label="Contact Email" text={site.contact?.email ?? ""} />
              </Grid>
            </Grid>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/** §15 LQG Site Closure, read-only. */
function FrozenSiteClosureSection({ closure }: { closure: ResolvedSiteClosure }) {
  return (
    <Box>
      <Typography variant="subtitle1" mb={2}>
        LQG Site Closure
      </Typography>
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Closure Type"
            text={closure.closureType ? CLOSURE_TYPE_LABELS[closure.closureType] : ""}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Expected Closure Date"
            text={formatDate(closure.expectedClosureDate)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Requested New Closure Date"
            text={formatDate(closure.requestingNewClosureDate)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField
            label="Actual Closure Date"
            text={formatDate(closure.actualClosureDate)}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

/** §16 HSM Notification, read-only. */
function FrozenHsmSection({ hsm }: { hsm: ResolvedHsmNotification }) {
  const activities = hsm.activities ?? [];

  return (
    <Box>
      <Typography variant="subtitle1" mb={2}>
        Hazardous Secondary Material (HSM)
      </Typography>
      <Grid container columnSpacing={3} rowSpacing={2} mb={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField label="Managing HSM" text={formatBool(hsm.isManagingHsm)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField label="Reason" text={hsm.reason ? HSM_REASON_LABELS[hsm.reason] : ""} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <DisplayOnlyField label="Effective Date" text={formatDate(hsm.effectiveDate)} />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" gutterBottom>
        HSM Activities
      </Typography>
      {activities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {EMPTY}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {activities.map((activity, index) => (
            <Grid container columnSpacing={3} rowSpacing={1} key={index}>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <DisplayOnlyField label="Facility Code" text={activity.facilityCode ?? ""} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <DisplayOnlyField
                  label="Estimated Short Tons"
                  text={activity.estimatedShortTons?.toString() ?? ""}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <DisplayOnlyField
                  label="Land-Based Unit"
                  text={
                    activity.landBasedUnit ? HSM_LAND_BASED_UNIT_LABELS[activity.landBasedUnit] : ""
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <DisplayOnlyField
                  label="Waste Codes"
                  renderValue={() => <CodeChips codes={activity.wasteCodes ?? []} />}
                />
              </Grid>
            </Grid>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Read-only render of the filed Filing Notifications (§13 episodic generation,
 * §15 LQG site closure, §16 HSM), sourced from the 8700-12 snapshot. Each section
 * appears only when it was filed (its gate was "Yes", so the snapshot carries a
 * non-null row); when none were filed, an empty-state message is shown.
 */
export function FrozenFilingNotificationsPanel({ resolved }: { resolved: Resolved870012Content }) {
  const episodicEvent = resolved.episodicEvent ?? null;
  const siteClosure = resolved.siteClosure ?? null;
  const hsmNotification = resolved.hsmNotification ?? null;
  // §14 is facility-scoped, carried on the snapshot's RCRA profile body. Shown
  // only when the site is a consolidator, matching the "shown only when filed"
  // rule of the submission-scoped sections.
  const isConsolidator = resolved.rcraProfile?.isLqgConsolidator === true;
  const consolidatedSites = resolved.rcraProfile?.consolidatedSites ?? [];
  const hasAny = Boolean(episodicEvent || isConsolidator || siteClosure || hsmNotification);

  return (
    <Box>
      <Typography variant="h6" mb={3}>
        📨 Filing Notifications
      </Typography>
      {!hasAny ? (
        <Typography variant="body2" color="text.secondary">
          No filing notifications were filed.
        </Typography>
      ) : (
        <Stack spacing={4}>
          {episodicEvent && <FrozenEpisodicSection event={episodicEvent} />}
          {isConsolidator && <FrozenLqgConsolidationSection sites={consolidatedSites} />}
          {siteClosure && <FrozenSiteClosureSection closure={siteClosure} />}
          {hsmNotification && <FrozenHsmSection hsm={hsmNotification} />}
        </Stack>
      )}
    </Box>
  );
}
